import classic from 'ember-classic-decorator';

import ApplicationSerializer from 'later/serializers/application';
import { convertApiTagToTag } from 'shared/utils/post-tagging/post-tagging';

import type Store from '@ember-data/store';
import type Snapshot from '@ember-data/store/snapshot';
// eslint-disable-next-line ember/use-ember-data-rfc-395-imports
import type { ModelSchema } from 'ember-data';
import type ModelRegistry from 'ember-data/types/registries/model';
import type { ApiTag, Tag } from 'shared/types/post-tagging';

@classic
export default class GramSerializer extends ApplicationSerializer {
  // Temporary storage for tags during normalization
  private _pendingTags: Map<string, Tag[]> = new Map();

  attrs = {
    postActivities: {
      embedded: 'always',
      serialize: 'records'
    },
    postMediaItems: {
      embedded: 'always',
      serialize: 'records'
    },
    linkinbioPost: {
      embedded: 'always',
      serialize: 'records'
    }
  };

  serializeAttribute<K extends keyof ModelRegistry>(
    snapshot: Snapshot<K>,
    json: object,
    key: string,
    attribute: object
  ): void {
    if (key === 'isStory') {
      // This serializer only ever receives gram snapshots.
      const gramSnapshot = snapshot as Snapshot<'gram'>;
      const profileSnapshot = gramSnapshot.belongsTo('socialProfile');
      // When the profile relationship isn't loaded, strip `is_story` — it's
      // only meaningful for Instagram, and the bug this guards against was
      // sending it for other platforms.
      if (profileSnapshot?.attr('profileType') !== 'instagram') {
        return;
      }
    }
    super.serializeAttribute(
      snapshot,
      json,
      key,
      attribute as Parameters<ApplicationSerializer['serializeAttribute']>[3]
    );
  }

  normalize(
    modelClass: ModelRegistry[keyof ModelRegistry],
    resourceHash: Record<string, unknown>,
    prop?: string
  ): ReturnType<ApplicationSerializer['normalize']> {
    // Store tags before normalization
    if (resourceHash.tags && Array.isArray(resourceHash.tags) && resourceHash.id) {
      const tags = resourceHash.tags.map((tag: ApiTag) => convertApiTagToTag(tag));
      this._pendingTags.set(String(resourceHash.id), tags);
    }

    return super.normalize(modelClass, resourceHash, prop || '');
  }

  normalizeResponse(
    store: Store,
    primaryModelClass: ModelSchema<keyof ModelRegistry>,
    payload: Record<string, unknown>,
    id: string | number,
    requestType: string
  ): ReturnType<ApplicationSerializer['normalizeResponse']> {
    const normalized = super.normalizeResponse(store, primaryModelClass, payload, id, requestType);

    // Set tags on model instances after they're pushed to the store
    // Use setTimeout to ensure records are available in the store

    this._pendingTags.forEach((tags, recordId) => {
      const record = store.peekRecord('gram', recordId);
      if (record) record.tags = tags;
    });
    this._pendingTags.clear();

    return normalized;
  }
}
